import classNames from "classnames";
import { getBrowserLanguage } from "common/Support";
import { convertDateFromTimeZoneAgnostic, getNowTimeZoneAgnostic } from "date-time/support";
import * as React from "react";
import { getFirstDayOfWeek } from "../first-day-of-week";
import { HorizontalCalendarStyle } from "./horizontal-calendar-style";

interface Props {
    readonly className?: string;
    readonly selected: Date;
    readonly onSelectionChange?: (date: Date) => void;
    readonly mode: "week" | "month";
    readonly canSwipe: boolean;
}

function getPickerPropsForWeek(date: Date, firstDayOfWeek: number, selectedDay: Date) {
    const dayOfWeek = date.getUTCDay();
    const offset = (7 + dayOfWeek - firstDayOfWeek) % 7;
    const startDay = new Date(new Date(date).setUTCDate(date.getUTCDate() - offset));
    const dayPicker = ["S", "M", "T", "W", "Th", "F", "S"].map((_d, i) => {
        const dayDate = new Date(new Date(startDay).setUTCDate(startDay.getUTCDate() + i));
        const day = dayDate.getUTCDay();
        return {
            label: Intl.DateTimeFormat(getBrowserLanguage(), { weekday: "narrow" }).format(
                convertDateFromTimeZoneAgnostic(dayDate)
            ),
            date: dayDate,
            day: dayDate.getUTCDate(),
            isWeekend: day === 0 || day === 6,
            isCurrentDay:
                getNowTimeZoneAgnostic().setUTCHours(0, 0, 0, 0) === new Date(dayDate).setUTCHours(0, 0, 0, 0),
            isSelected: new Date(dayDate).setUTCHours(0, 0, 0, 0) === new Date(selectedDay).setUTCHours(0, 0, 0, 0),
        };
    });
    return dayPicker;
}

const HorizontalCalendar: React.FunctionComponent<Props> = p => {
    const { className, selected, onSelectionChange } = p;

    const [dragging, setDragging] = React.useState(false);
    const [moveStart, setMoveStart] = React.useState<[number, number]>();
    const [dragOffset, setDragOffset] = React.useState(0);
    const [dayOffset, setDayOffset] = React.useState(0);

    const firstDayOfWeek = React.useMemo(getFirstDayOfWeek, []);

    React.useLayoutEffect(() => {
        setDragOffset(0);
        setDayOffset(0);
    }, [selected]);

    const [prevWeekDate, nextWeekDate] = React.useMemo(() => {
        return [
            new Date(new Date(selected).setUTCDate(selected.getUTCDate() - 7 + dayOffset)),
            new Date(new Date(selected).setUTCDate(selected.getUTCDate() + 7 + dayOffset)),
        ] as const;
    }, [selected, dayOffset]);

    const prevWeek = getPickerPropsForWeek(prevWeekDate, firstDayOfWeek, selected);
    const dayPicker = getPickerPropsForWeek(
        new Date(new Date(selected).setUTCDate(selected.getUTCDate() + dayOffset)),
        firstDayOfWeek,
        selected
    );
    const nextWeek = getPickerPropsForWeek(nextWeekDate, firstDayOfWeek, selected);

    const mapPicker = (picker: typeof dayPicker) => {
        return picker.map(d => (
            <div
                key={d.day}
                className={classNames(
                    "cal-day-picker",
                    d.isCurrentDay && "today",
                    d.isSelected && "selected",
                    d.isWeekend && "weekend"
                )}>
                <div className="day-label" aria-hidden={true}>
                    {d.label}
                </div>
                <div className="day-number" onClick={() => onSelectionChange?.(d.date)}>
                    {d.day}
                </div>
            </div>
        ));
    };

    const goTarget = React.useCallback(
        (target: number, onFinish?: () => void) => {
            let last: number | undefined;
            let lastSetOffset = dragOffset;
            const step = (time: number) => {
                if (last === undefined) {
                    last = time;
                    window.requestAnimationFrame(step);
                    return;
                }
                const timestep = time - last;
                const motion = timestep / 8;

                let newValue: number;
                if (target > lastSetOffset) {
                    newValue = Math.min(target, lastSetOffset + motion);
                } else {
                    newValue = Math.max(target, lastSetOffset - motion);
                }

                setDragOffset(newValue);
                lastSetOffset = newValue;

                if (newValue !== target) {
                    window.requestAnimationFrame(step);
                } else {
                    onFinish?.();
                }
            };

            window.requestAnimationFrame(step);
        },
        [dragOffset]
    );

    const onSlideDragStart = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            setMoveStart([event.clientX - dragOffset, event.clientY]);
        },
        [dragOffset]
    );

    const onSlideMouseMove = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (moveStart === undefined || event.buttons % 2 !== 1) return;

            const [startX] = moveStart;
            const deltaX = Math.abs(event.clientX - startX);

            if (deltaX > 15 && !dragging) {
                setDragging(true);
            }

            if (dragging || deltaX > 15) {
                setDragOffset(event.clientX - startX);
            }
        },
        [dragging, moveStart]
    );

    const onSlideDragEnd = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!dragging) return;
            const slide = event.currentTarget;

            event.stopPropagation();
            event.preventDefault();

            if (dragOffset > slide.clientWidth / 3) {
                goTarget(slide.clientWidth, () => {
                    setDayOffset(dayOffset - 7);
                    setDragOffset(0);
                    onSelectionChange?.(prevWeekDate);
                });
            } else if (-dragOffset > slide.clientWidth / 3) {
                goTarget(-slide.clientWidth, () => {
                    setDayOffset(dayOffset + 7);
                    setDragOffset(0);
                    onSelectionChange?.(nextWeekDate);
                });
            } else {
                goTarget(0);
            }

            setMoveStart(undefined);
            setDragging(false);
        },
        [dayOffset, dragOffset, dragging, goTarget, nextWeekDate, onSelectionChange, prevWeekDate]
    );

    const slideStyle = React.useMemo<React.CSSProperties>(() => {
        return {
            transform: `translateX(${dragOffset}px)`,
        };
    }, [dragOffset]);

    return (
        <HorizontalCalendarStyle className={className}>
            <div
                className="slide"
                style={slideStyle}
                onMouseDown={onSlideDragStart}
                onMouseMove={onSlideMouseMove}
                onClickCapture={onSlideDragEnd}
                onMouseLeave={onSlideDragEnd}>
                <div className="cal">{mapPicker(dayPicker)}</div>
                <div className="cal prev">{mapPicker(prevWeek)}</div>
                <div className="cal next">{mapPicker(nextWeek)}</div>
            </div>
        </HorizontalCalendarStyle>
    );
};

export default HorizontalCalendar;
